<template>
  <div class="backdrop" @click.self="$emit('close')">
    <div class="modal">
      <button class="x" @click="$emit('close')" aria-label="Close">×</button>

      <div
        class="top"
        :class="{ topLink: canOpenAccountDetail }"
        :role="canOpenAccountDetail ? 'button' : undefined"
        :tabindex="canOpenAccountDetail ? 0 : undefined"
        :title="canOpenAccountDetail ? 'Open account details' : undefined"
        @click="openAccountDetail"
        @keydown.enter.prevent="openAccountDetail"
        @keydown.space.prevent="openAccountDetail"
      >
        <FavIcon :domain="signal?.account_domain || ''" :size="38" />
        <div class="head">
          <div class="accountTitle">{{ accountName }}</div>
          <div v-if="contactEmail" class="accountContact" :title="contactEmail">
            <HeroIcon name="UserIcon" iconStyle="solid" :size="14" color="#9ca3af" />
            <span class="accountContactText">{{ contactEmail }}</span>
          </div>
        </div>
      </div>

      <div class="section">
        <div class="label">Signal</div>
        <div class="value">
          <div class="signalName" :title="signal?.name || ''">{{ signalName }}</div>
          <span v-if="sourceName" class="sourceInline" :title="`via ${sourceName}`">
            <span class="via">via</span>
            <HeroIcon
              v-if="isAgentSource"
              :name="agentIcon?.icon_name"
              :iconStyle="agentIcon?.icon_style"
              :color="agentIcon?.icon_color"
              :size="16"
            />
            <img
              v-else-if="sourceIconUrl"
              :src="sourceIconUrl"
              class="sourceIcon"
              :alt="sourceName"
              loading="lazy"
              draggable="false"
            />
            <span class="sourceName">{{ sourceName }}</span>
          </span>
        </div>
      </div>

      <div class="section">
        <div class="label">Occurred</div>
        <div class="value">{{ formatTs(signal?.occurred_at) }}</div>
      </div>

      <div class="section">
        <div class="label">Description</div>
        <pre class="json">{{ prettyDescription }}</pre>
      </div>

      <div v-if="Number(signal?.triggered_count || 0) > 0" class="section">
        <div class="label">Triggers fired</div>
        <div class="value">
          <div v-if="triggerRunsLoading" class="muted">Loading…</div>
          <div v-else-if="triggerRunsError" class="muted">{{ triggerRunsError }}</div>
          <div v-else class="triggerRuns">
            <div v-for="r in triggerRuns" :key="r.run_id" class="triggerRun" @click="openRun(r.run_id)">
              <div class="triggerRunTop" :title="triggerRunTitle(r)">
                <div class="triggerNamePill">
                  <HeroIcon name="BoltIcon" iconStyle="solid" :size="14" color="#7c3aed" />
                  <span class="triggerNameText">{{ r.trigger_name || 'Trigger' }}</span>
                </div>
                <span v-if="r.agent_name" class="triggerArrow">→</span>
                <div v-if="r.agent_name" class="triggerAgent">
                  <HeroIcon
                    v-if="triggerAgentIcon(r)?.icon_name"
                    :name="triggerAgentIcon(r)?.icon_name"
                    :iconStyle="triggerAgentIcon(r)?.icon_style"
                    :color="triggerAgentIcon(r)?.icon_color"
                    :size="16"
                  />
                  <span class="triggerAgentName">{{ r.agent_name }}</span>
                </div>

                <button class="btn viewRunBtn" @click.stop="openRun(r.run_id)">View Agent Run</button>
              </div>
              <div v-if="r.created_at" class="muted">{{ formatTs(r.created_at) }}</div>
            </div>
          </div>
        </div>
      </div>

      <div class="footer">
        <div class="left"></div>
        <div class="right">
          <button class="btn" @click="$emit('close')">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import FavIcon from './FavIcon.vue'
import HeroIcon from './HeroIcon.vue'
import { formatSignalBaseName } from '../services/signal_format.ts'
import { apiFetch } from '../services/api.ts'
import { googleFaviconUrl } from '../services/favicons.ts'

const props = defineProps({
  signal: { type: Object, required: true },
  accountIdByDomain: { type: Object, default: () => ({}) },
  agentById: { type: Object, default: () => ({}) },
})

const router = useRouter()

const accountName = computed(() => {
  return String(props.signal?.account_name || props.signal?.account_domain || '').trim()
})

const accountId = computed(() => {
  const domain = String(props.signal?.account_domain || '').trim()
  if (!domain) return ''
  return String(props.accountIdByDomain?.[domain] || '').trim()
})

const canOpenAccountDetail = computed(() => Boolean(accountId.value))

const contactEmail = computed(() => {
  const d = props.signal?.description
  if (!d || typeof d !== 'object') return ''

  const candidates = [
    d.contact_email,
    d.who_email,
    d.email,
    d.Email,
    d['Business Email'],
    d['Email'],
    d.contact,
  ]
  for (const v of candidates) {
    if (typeof v === 'string') {
      const cleaned = v.trim()
      if (cleaned && cleaned.includes('@')) return cleaned
    }
  }

  const cm = d.campaign_member
  if (cm && typeof cm === 'object') {
    const c = cm.Contact
    if (c && typeof c === 'object') {
      const e = typeof c.Email === 'string' ? c.Email.trim() : ''
      if (e && e.includes('@')) return e
    }
  }

  return ''
})

function formatTs(ts) {
  if (!ts) return ''
  try {
    return new Date(ts).toLocaleString()
  } catch {
    return String(ts)
  }

}

function openRun(runId) {
  const id = String(runId || '').trim()
  if (!id) return
  router.push(`/agent-runs/${encodeURIComponent(id)}`)
}

function openAccountDetail() {
  if (!canOpenAccountDetail.value) return
  router.push(`/accounts/${encodeURIComponent(accountId.value)}`)
}

const isAgentSource = computed(() => String(props.signal?.source_type || '').trim().toLowerCase() === 'agent')

const sourceAgent = computed(() => {
  const aid = props.signal?.source_agent_id ? String(props.signal.source_agent_id) : ''
  if (!aid) return null
  return props.agentById?.[aid] || null
})

const agentIcon = computed(() => {
  const a = sourceAgent.value
  return {
    icon_name: a?.icon_name || null,
    icon_style: a?.icon_style || null,
    icon_color: a?.icon_color || null,
  }
})

const sourceName = computed(() => {
  if (isAgentSource.value) {
    const a = sourceAgent.value
    return String(a?.name || '').trim() || String(props.signal?.source_label || 'agent').trim()
  }
  return String(props.signal?.source_label || props.signal?.source_type || '').trim()
})

const sourceIconUrl = computed(() => {
  const raw = String(props.signal?.source_label || props.signal?.source_type || '').trim().toLowerCase()
  if (!raw) return ''
  if (raw.includes('salesforce')) return googleFaviconUrl('salesforce.com', { size: 32 })
  if (raw.includes('warmly')) return googleFaviconUrl('warmly.ai', { size: 32 })
  if (raw.includes('hubspot')) return googleFaviconUrl('hubspot.com', { size: 32 })
  if (raw.includes('gmail') || raw.includes('google')) return googleFaviconUrl('google.com', { size: 32 })
  return ''
})

const signalName = computed(() => formatSignalBaseName(props.signal))

const prettyDescription = computed(() => {
  const d = props.signal?.description
  try {
    return JSON.stringify(d ?? {}, null, 2)
  } catch {
    return String(d || '')
  }
})

const triggerRuns = ref([])
const triggerRunsLoading = ref(false)
const triggerRunsError = ref('')

function agentIconById(agentId) {
  const aid = agentId ? String(agentId) : ''
  const a = aid ? props.agentById?.[aid] : null
  return {
    icon_name: a?.icon_name || null,
    icon_style: a?.icon_style || null,
    icon_color: a?.icon_color || null,
  }
}

function triggerAgentIcon(r) {
  const byId = agentIconById(r?.agent_id)
  if (byId?.icon_name) return byId
  return {
    icon_name: r?.agent_icon_name || null,
    icon_style: r?.agent_icon_style || null,
    icon_color: r?.agent_icon_color || null,
  }
}

function triggerRunTitle(r) {
  const tn = String(r?.trigger_name || '').trim()
  const an = String(r?.agent_name || '').trim()
  if (tn && an) return `${tn} → ${an}`
  return tn || an || ''
}

async function loadTriggerRuns(signalId) {
  triggerRuns.value = []
  triggerRunsError.value = ''
  if (!signalId) return
  if (Number(props.signal?.triggered_count || 0) <= 0) return

  triggerRunsLoading.value = true
  try {
    const resp = await apiFetch(`/signals/${signalId}/trigger_runs`)
    triggerRuns.value = Array.isArray(resp?.runs) ? resp.runs : []
  } catch (e) {
    triggerRunsError.value = e?.message || 'Failed'
    triggerRuns.value = []
  } finally {
    triggerRunsLoading.value = false
  }
}

watch(
  () => props.signal?.id,
  async (id) => {
    try {
      await loadTriggerRuns(id)
    } catch {
    }
  },
  { immediate: true },
)
</script>

<style scoped>
.backdrop {
  position: fixed;
  inset: 0;
  background: var(--backdrop);
  display: grid;
  place-items: center;
  padding: 18px;
  z-index: 60;
}

.modal {
  width: min(860px, 100%);
  max-height: calc(100vh - 36px);
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 16px;
  box-shadow: 0 10px 30px var(--shadow);
  padding: 18px;
  position: relative;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.x {
  position: absolute;
  top: 10px;
  right: 10px;
  border: none;
  background: transparent;
  font-size: 26px;
  line-height: 1;
  cursor: pointer;
  color: var(--muted);
}

.top {
  display: flex;
  gap: 12px;
  align-items: center;
  padding-right: 28px;
}

.topLink {
  cursor: pointer;
  border-radius: 10px;
  padding: 8px;
  margin: -8px;
  transition: background-color 120ms ease, box-shadow 120ms ease;
}

.topLink:hover {
  background: var(--row-hover-bg);
}

.topLink:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px var(--focus-ring);
  background: var(--row-hover-bg);
}

.head {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.accountTitle {
  font-weight: 700;
  font-size: 20px;
}

.accountContact {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--muted);
}

.accountContactText {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 600px;
}

.title {
  font-weight: 700;
  font-size: 20px;
}

.sub {
  color: var(--muted);
}

.section {
  margin-top: 16px;
}

.label {
  font-weight: 700;
  margin-bottom: 8px;
}

.value {
  color: var(--text);
}

.signalName {
  font-weight: 700;
  font-size: 18px;
  color: var(--text);
  margin-bottom: 6px;
}

.sourceInline {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.via {
  color: var(--muted);
}

.sourceIcon {
  width: 16px;
  height: 16px;
  border-radius: 4px;
  display: inline-block;
}

.sourceName {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 560px;
}

.muted {
  color: var(--muted);
}

.json {
  margin: 0;
  padding: 12px;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: var(--md-pre-bg);
  max-height: 380px;
  overflow: auto;
  font-size: 13px;
  line-height: 1.5;
  color: var(--md-pre-text);
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;
  max-width: 100%;
}

.triggerRuns {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.triggerRun {
  padding: 10px 12px;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: var(--surface);
}

.triggerRunTop {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
}

.viewRunBtn {
  margin-left: auto;
}


.triggerNamePill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: #7c3aed;
  font-size: 12px;
  line-height: 1;
  min-width: 0;
  max-width: 100%;
}

.triggerNameText {
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 360px;
}

.triggerArrow {
  color: var(--muted);
  flex: none;
}

.triggerAgent {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  max-width: 100%;
  color: var(--text);
}

.triggerAgentName {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 360px;
}

.footer {
  margin-top: 18px;
  border-top: 1px solid var(--border);
  padding-top: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.right {
  display: flex;
  gap: 8px;
}
</style>
